import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TablePaginationConfig, TableProps } from 'antd';
import { DEFAULT_TABLE_PAGINATION } from '../utils/tableConfig';

export const ADMIN_TABLE_ROW_HIGHLIGHT_CLASS = 'admin-table-row-highlight';

const HIGHLIGHT_MS = 2800;

export type AdminTableStabilityOptions = {
  /** Initial page size (default from DEFAULT_TABLE_PAGINATION). */
  pageSize?: number;
};

/**
 * Solution A: keep list page after reload + flash the mutated row so edits don't "disappear".
 */
export function useAdminTableStability(options: AdminTableStabilityOptions = {}) {
  const initialPageSize = options.pageSize ?? DEFAULT_TABLE_PAGINATION.pageSize ?? 20;
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const flashRow = useCallback((id: string | null | undefined) => {
    if (!id) return;
    const key = String(id);
    setHighlightId(key);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setHighlightId((prev) => (prev === key ? null : prev));
      timerRef.current = null;
    }, HIGHLIGHT_MS);

    // Ant Design sets data-row-key on <tr>
    window.setTimeout(() => {
      const el = document.querySelector(
        `.ant-table-tbody > tr.ant-table-row[data-row-key="${CSS.escape(key)}"]`
      );
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 80);
  }, []);

  const pagination: TablePaginationConfig = useMemo(
    () => ({
      ...DEFAULT_TABLE_PAGINATION,
      current,
      pageSize,
      onChange: (page, size) => {
        setCurrent(page);
        if (typeof size === 'number') setPageSize(size);
      },
    }),
    [current, pageSize]
  );

  // Loosely typed so generic Table<T> pages accept the handler (pagination only).
  const onTableChange = useCallback((pag: TablePaginationConfig) => {
    if (typeof pag?.current === 'number') setCurrent(pag.current);
    if (typeof pag?.pageSize === 'number') setPageSize(pag.pageSize);
  }, []) as NonNullable<TableProps<any>['onChange']>;

  const rowClassName = useCallback(
    (record: { id?: string; auth_uid?: string }, _index: number): string => {
      const key = record.id ?? record.auth_uid;
      if (!key || !highlightId) return '';
      return String(key) === highlightId ? ADMIN_TABLE_ROW_HIGHLIGHT_CLASS : '';
    },
    [highlightId]
  );

  /** Prefer after await reload() so the row exists in the DOM. */
  const afterMutation = useCallback(
    async (id: string | null | undefined, reload?: () => void | Promise<void>) => {
      if (reload) await reload();
      flashRow(id);
    },
    [flashRow]
  );

  return {
    pagination,
    onTableChange,
    rowClassName,
    flashRow,
    afterMutation,
    highlightId,
  };
}
